import React, { useReducer, useRef } from 'react';
import cn from 'classnames';
import { CheckIcon, LoaderIcon } from '@components/Icons';
import { apiRequest } from '@support/api';
import { Section, SettingsSectionBox, SettingsSectionLabel } from '@views/Settings/components';
import styles from './WorkspaceName.module.scss';
import { useWorkspaceContext } from '@support/hooks/use-workspace-context';
import { WorkspaceAPIItem } from '@models/Workspace';

interface WorkspaceNameSettingState {
  editing: boolean;
  saving: boolean;
  value: string;
  initialValue: string;
  isChanged: boolean;
  errorMsg?: string;
  loadError: boolean;
}

const reducer = (
  state: WorkspaceNameSettingState,
  [type, value]: [string, any?],
): WorkspaceNameSettingState => {
  switch (type) {
    case 'currentWorkspaceNameLoaded':
      return {
        ...state,
        saving: false,
        value,
        initialValue: value,
        isChanged: false,
      };

    case 'edit':
      return { ...state, value, isChanged: value != state.initialValue, errorMsg: null };

    case 'save':
      return { ...state, editing: false, saving: true };

    case 'cancel':
      return {
        ...state,
        value: state.initialValue,
        editing: false,
        errorMsg: null,
        isChanged: false,
      };

    case 'error':
      return {
        ...state,
        saving: false,
        value: state.initialValue,
        isChanged: false,
        errorMsg: 'Something went wrong when saving your workspace name! Please try again.',
      };

    case 'validationError':
      return { ...state, errorMsg: value };

    case 'loadError':
      return { ...state, loadError: true };

    default:
      return state;
  }
};

export default function WorkspaceName() {
  const { activeWorkspace, updateActiveWorkspace, userIsAdmin } = useWorkspaceContext();
  const [{ value, saving, isChanged, errorMsg, loadError }, dispatch] = useReducer(reducer, {
    editing: false,
    saving: false,
    value: activeWorkspace?.name,
    initialValue: activeWorkspace?.name,
    isChanged: false,
    loadError: false,
  });
  const inputRef = useRef(null);

  const saveChanges = () => {
    if (inputRef.current && inputRef.current.validity.valueMissing) {
      return dispatch(['validationError', 'A workspace name is required']);
    }

    const originalName = activeWorkspace?.name;
    updateActiveWorkspace({ name: value });
    dispatch(['save']);

    apiRequest<WorkspaceAPIItem>(
      'PATCH',
      '/workspaces/active/',
      { name: value },
      { apiVersion: 'v2' },
    )
      .then((workspace) => {
        dispatch(['currentWorkspaceNameLoaded', workspace.name || '']);
      })
      .catch(() => {
        updateActiveWorkspace({ name: originalName });
        dispatch(['error']);
      });
  };

  const handleInputKeyDown = (e) => {
    // "Enter"
    if (e.keyCode == 13) {
      saveChanges();
    }

    // "Esc"
    if (e.keyCode == 27) {
      dispatch(['cancel']);
    }
  };

  return (
    !loadError && (
      <Section>
        <SettingsSectionLabel error={errorMsg}>Name</SettingsSectionLabel>
        {userIsAdmin ? (
          <SettingsSectionBox className={styles.inputWrapper}>
            <input
              className={cn(styles.textInput, isChanged && styles.dirtyInput)}
              type="text"
              required
              ref={inputRef}
              value={value}
              placeholder={`Enter Workspace Name`}
              onChange={(e) => dispatch(['edit', e.target.value])}
              onKeyDown={handleInputKeyDown}
              disabled={saving}
            />
            <div className={styles.btnContainer}>
              {saving && <LoaderIcon />}
              {isChanged && value.length > 0 && !saving && (
                <CheckIcon
                  width="14"
                  className={styles.iconBtn}
                  tabIndex={0}
                  onClick={saveChanges}
                />
              )}
            </div>
          </SettingsSectionBox>
        ) : (
          <SettingsSectionBox className={styles.readOnlyName}>{value}</SettingsSectionBox>
        )}
      </Section>
    )
  );
}
